import type {
    ImageProps as LibImageProps,
    StyleProps,
} from "@mallardbay/lib-react-components"
import type { SyntheticEvent } from "react"
import React, { useCallback, useMemo, useState } from "react"
import {
    Image as LibImage,
    useImagePlaceholder,
} from "@mallardbay/lib-react-components"

import { getSentryClient, SENTRY_METRICS } from "~utils/sentry"
import { TEST_IDS, UPLOAD_CARE_BASE_DOMAIN } from "~config/constants"

type WidthHeightValue = LibImageProps["width"] | string | number

type LibImageWithErrorHandler = LibImageProps & {
    readonly onError?: React.ReactEventHandler<HTMLImageElement>
}

/**
 * Dashboard `Image` wraps lib `Image` and accepts legacy Chakra-style props (`sx`, `w`, `ignoreFallback`, etc.).
 */
export type DashboardImageProps = Omit<LibImageProps, "style" | "width"> & {
    readonly style?: StyleProps
    readonly sx?: StyleProps
    readonly w?: string | number
    readonly h?: string | number
    readonly width?: WidthHeightValue
    readonly height?: WidthHeightValue
    readonly borderRadius?: string
    readonly objectFit?: string
    readonly rounded?: string
    /** Chakra `Image` prop; maps to lib `showFallback` inverted. */
    readonly ignoreFallback?: boolean
    readonly onError?: React.ReactEventHandler<HTMLImageElement>
}

// Modeled after shared/image.tsx in web. Keep them aligned so we can unify when we use a shared component lib
// eslint-disable-next-line max-lines-per-function -- TODO: Refactor into smaller functions
export default function Image({
    src,
    onLoad,
    onError,
    skeletonStyles = {},
    style,
    sx,
    w: widthChakraShorthand,
    h: heightChakraShorthand,
    width,
    height,
    borderRadius,
    objectFit,
    rounded,
    ignoreFallback,
    testId = TEST_IDS.IMAGE,
    shouldSkipUploadCareResize,
    showFallback,
    ...rest
}: DashboardImageProps) {
    const [isImageLoaded, setIsImageLoaded] = useState(false)
    const placeholderSrc = useImagePlaceholder()
    const srcToUse = src || placeholderSrc
    const isUploadCareImage = useMemo(
        () => src?.includes(UPLOAD_CARE_BASE_DOMAIN),
        [src]
    )

    const wrappedOnLoad = useCallback(
        (event: SyntheticEvent<HTMLImageElement, Event>) => {
            setIsImageLoaded(true)
            onLoad?.(event)
        },
        [onLoad]
    )

    const wrappedOnError = useCallback(
        (event: SyntheticEvent<HTMLImageElement, Event>) => {
            getSentryClient().metrics.count(
                SENTRY_METRICS.IMAGE_LOAD_ERROR,
                1,
                {
                    attributes: {
                        is_uploadcare: String(Boolean(isUploadCareImage)),
                    },
                }
            )
            onError?.(event)
        },
        [isUploadCareImage, onError]
    )

    const layoutStyle = useMemo((): StyleProps => {
        const next: StyleProps = { ...(sx ?? {}) }

        if (widthChakraShorthand !== undefined) {
            next.width = widthChakraShorthand
        }

        if (heightChakraShorthand !== undefined) {
            next.height = heightChakraShorthand
        }

        if (width !== undefined) {
            next.width = width
        }

        if (height !== undefined) {
            next.height = height
        }

        if (borderRadius !== undefined) {
            next.borderRadius = borderRadius
        }

        if (objectFit !== undefined) {
            next.objectFit = objectFit
        }

        if (rounded !== undefined) {
            next.borderRadius = rounded
        }

        return next
    }, [
        sx,
        widthChakraShorthand,
        heightChakraShorthand,
        width,
        height,
        borderRadius,
        objectFit,
        rounded,
    ])

    const mergedStyle = useMemo(
        () => ({
            ...layoutStyle,
            ...style,
            display: isImageLoaded ? "initial" : "none",
        }),
        [isImageLoaded, layoutStyle, style]
    )

    const showFallbackResolved =
        ignoreFallback === true ? false : showFallback ?? true

    const numericWidth = typeof width === "number" ? width : undefined

    const libProps: LibImageWithErrorHandler = {
        ...rest,
        testId,
        src: srcToUse,
        onLoad: wrappedOnLoad,
        onError: wrappedOnError,
        skeletonStyles,
        style: mergedStyle,
        shouldSkipUploadCareResize,
        showFallback: showFallbackResolved,
        width: numericWidth,
    }

    return <LibImage {...libProps} />
}
